import {
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { tv } from '@/utils/tv';

const container = tv({
  base: 'relative inline-flex h-10 items-stretch rounded-[20px] bg-popover p-0.5 shadow-card',
});

const tab = tv({
  base: 'text-subhead-14 relative z-10 flex min-w-0 cursor-pointer items-center justify-center rounded-[20px] px-5 whitespace-nowrap transition-colors duration-200 ease-[cubic-bezier(0.4,0,0.2,1)]',
  variants: {
    isActive: {
      true: 'text-primary-foreground',
      false: 'text-foreground',
    },
  },
});

export interface SegmentedTabItem<T extends string> {
  value: T;
  label: string;
  trailing?: ReactNode;
}

interface Props<T extends string> {
  items: Array<SegmentedTabItem<T>>;
  value: T;
  className?: string;
  tabClassName?: string;
  onChange: (value: T) => void;
}

export function SegmentedTabs<T extends string>({
  items,
  value,
  className,
  tabClassName,
  onChange,
}: Props<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [indicator, setIndicator] = useState<{
    left: number;
    width: number;
  } | null>(null);

  const activeIndex = items.findIndex((item) => item.value === value);

  useLayoutEffect(() => {
    const node = tabRefs.current[activeIndex];
    if (!node) {
      return;
    }
    setIndicator({ left: node.offsetLeft, width: node.offsetWidth });
  }, [activeIndex]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) {
      return;
    }

    const observer = new ResizeObserver(() => {
      const activeNode = tabRefs.current[activeIndex];
      if (!activeNode) {
        return;
      }
      setIndicator({
        left: activeNode.offsetLeft,
        width: activeNode.offsetWidth,
      });
    });

    observer.observe(node);

    return () => observer.disconnect();
  }, [activeIndex]);

  return (
    <div ref={containerRef} className={container({ className })}>
      {indicator && (
        <span
          aria-hidden
          style={{ left: indicator.left, width: indicator.width }}
          className="absolute top-0.5 bottom-0.5 rounded-[20px] bg-primary shadow-[0_1px_4px_0_rgba(0,0,0,0.1)] transition-[left,width] duration-200 ease-[cubic-bezier(0.4,0,0.2,1)]"
        />
      )}
      {items.map((item, index) => (
        <button
          key={item.value}
          type="button"
          ref={(node) => {
            tabRefs.current[index] = node;
          }}
          onClick={() => onChange(item.value)}
          className={tab({
            isActive: item.value === value,
            className: tabClassName,
          })}
        >
          <span className="relative flex min-w-0 items-center">
            <span className="truncate">{item.label}</span>
            {item.trailing}
          </span>
        </button>
      ))}
    </div>
  );
}
